import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Coffee, LogOut, Megaphone, MessageSquare } from "lucide-react";
import { useEffect, useState } from "react";
import { api, unwrap } from "./lib/api";
import { cn } from "./lib/utils";
import { AdminPage } from "./pages/AdminPage";
import { CampaignsPage } from "./pages/CampaignsPage";
import { ChatPage } from "./pages/ChatPage";
import { LoginPage } from "./pages/LoginPage";
import { Spinner } from "./components/ui/primitives";

type Tab = "chat" | "campaigns" | "admin";
const TABS: Array<{ id: Tab; label: string; icon: typeof Coffee }> = [
  { id: "chat", label: "Copiloto", icon: MessageSquare },
  { id: "campaigns", label: "Campañas", icon: Megaphone },
  { id: "admin", label: "Panel", icon: BarChart3 },
];

function useHashTab(): [Tab, (t: Tab) => void] {
  const read = (): Tab => {
    const h = window.location.hash.replace("#", "");
    return h === "campaigns" || h === "admin" ? h : "chat";
  };
  const [tab, setTab] = useState<Tab>(read);
  useEffect(() => {
    const onHash = () => setTab(read());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return [tab, (t) => (window.location.hash = t === "chat" ? "" : t)];
}

export function App() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: async () => unwrap(await api.auth.me.$get()) });
  const [tab, setTab] = useHashTab();

  if (me.isLoading) {
    return (
      <div className="grid h-full place-items-center text-roast">
        <Spinner />
      </div>
    );
  }
  if (!me.data?.authenticated) return <LoginPage onSuccess={() => qc.invalidateQueries({ queryKey: ["me"] })} />;

  const logout = async () => {
    await api.auth.logout.$post();
    qc.clear();
    await qc.invalidateQueries({ queryKey: ["me"] });
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-3 border-b border-latte bg-espresso px-4 py-2.5 text-foam">
        <div className="flex items-center gap-2">
          <span className="grid size-8 place-items-center rounded-lg bg-caramel/90">
            <Coffee className="size-4.5" />
          </span>
          <div className="leading-tight">
            <p className="text-sm font-semibold">Café Alborada</p>
            <p className="text-[11px] text-latte/80">Copiloto de negocio</p>
          </div>
        </div>
        <nav className="ml-auto flex items-center gap-1" aria-label="Secciones">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              aria-current={tab === id ? "page" : undefined}
              className={cn(
                "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm transition",
                tab === id ? "bg-foam/15 text-foam" : "text-latte/80 hover:bg-foam/10",
              )}
            >
              <Icon className="size-4" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
          <button onClick={logout} className="ml-1 rounded-lg p-1.5 text-latte/80 hover:bg-foam/10" aria-label="Cerrar sesión" title="Cerrar sesión">
            <LogOut className="size-4" />
          </button>
        </nav>
      </header>
      <main className="min-h-0 flex-1">
        {tab === "chat" && <ChatPage />}
        {tab === "campaigns" && <CampaignsPage />}
        {tab === "admin" && <AdminPage />}
      </main>
    </div>
  );
}
